/* ==========================================================================================
   Commercial Equipment Online 2026 · ce-product-delivery.js · the product page's DELIVERY panel
   (snippets/ce-product-delivery.liquid). Product builder, 09-10-2026. Deferred, no dependencies.

   A PORT of the delivery half of the live theme's assets/gst-toggle.js (hub amendments A307d/e/k/l, A316, A325 a-i,
   A331, A333, A334, A335, A344, A356, A392, A510, A580, A583, A587, A748). The rules are carried over unchanged; the
   reasons are summarised beside each one because every one of them was a real mistake Tommy caught on the live page.
   What changed for this theme:
     * Dawn markup: the variant id comes from the product form's [data-bop-variant-id]; the "FREE DELIVERY" pill goes
       into [data-cpd-freepill-slot] beside the GST note.
     * ONE postcode box drives the Shipping Rates Calculator app's own form (its State select is set from the postcode,
       the same postcode -> state bands as the live cart's A680, so the app never sees a mismatched state). The app's
       own form stays in the page; if its fields are not found, the panel steps aside and the app shows itself.
     * The panel listens to its own slot (the app's rows) and to option changes, not to the whole page.
     * Federal's branch suburbs are only named for the brands Federal supplies (data-cpd-fed): Bromic carries the same
       stock metafield, and its collection comes from the app's own rate, whose words name its real depot.
   Nothing here creates a cart, an order or a quote: the app prices a destination, that is all.
   ========================================================================================== */
(() => {
  'use strict';
  if (window.CEDelivery) return;
  const W = window, D = document;
  const money = (c) => (W.BO && W.BO.money ? W.BO.money(c) : '$' + ((Number(c) || 0) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','));

  /* ---------- the app's rate rows ---------- */
  // ☠️ A307k/A344: a $0.00 is NEVER a price on its own. The store's placeholder rates are $0.00 and the quote wording
  // has been reworded under the check before (1,793 products advertised FREE delivery for a day). A zero means FREE
  // only on the STANDARD tile and only when the rate's own NAME says "free" (the app's freeName(), a closed set).
  const PREM_RE = /premium|in[-\s]?store|inside|white\s*glove|two[-\s]?man|2[-\s]?man/i;
  // kept EXACTLY as live: widening it (e.g. "not included") would also catch a real, priced rate whose description
  // happens to say so, and print "Calculated after ordering" over a real price. Zeros are handled below instead.
  const QUOTE_RE = /calculated after|request a quote|quote to confirm/i;
  // A510: express is a WHITELIST (only warehouse stock has an express service; dropship never does)
  const EXPR_RE = /express/i;
  // A580: a collection is not a delivery and must never take the standard bucket (it arrives first at $0.00)
  const COLLECT_RE = /\bcollect(?:ion)?\b/i;
  // A356: the transit estimate is the carrier's own bracketed phrase, and it must name a unit of time
  const TRANSIT_RE = /\(([^()]*\b(?:days?|weeks?|hours?)\b[^()]*)\)/i;
  const methodOf = (name) => {
    if (PREM_RE.test(name || '')) return 'premium';
    if (EXPR_RE.test(name || '')) return 'express';
    if (COLLECT_RE.test(name || '')) return 'collect';
    return 'standard';
  };
  const transitFrom = (name) => { const m = TRANSIT_RE.exec(name || ''); return m ? m[1].trim() : ''; };

  const scrapeRates = (panel) => {
    const out = {};
    const scope = panel.querySelector('[data-delivery-calc-slot]') || D;
    scope.querySelectorAll('.oct-shipping-rate').forEach((row) => {
      const n = row.querySelector('.oct-shipping-rate__name');
      const p = row.querySelector('.oct-shipping-rate__price');
      if (!n) return;
      // A307l: title and description are two different things (the estimate is in the TITLE)
      const tEl = n.querySelector('.oct-shipping-rate__title');
      const dEl = n.querySelector('.oct-shipping-rate__description');
      const name = ((tEl || n).textContent || '').trim();
      const desc = dEl ? (dEl.textContent || '').trim() : '';
      const text = p ? (p.textContent || '').trim() : '';
      const num = parseFloat(text.replace(/[^0-9.]/g, ''));
      const m = methodOf(name);
      if (out[m]) return;                                   // first rate per method wins
      out[m] = { name, desc, transit: transitFrom(name), text, amount: isFinite(num) ? num : null,
        quoted: QUOTE_RE.test(name) || QUOTE_RE.test(desc) || QUOTE_RE.test(text) };
    });
    return out;
  };
  // the seam for another workstream: window.__RB_SHIP_RATES__ + document event 'rb:ship' (kept from live)
  const shipData = (panel) => {
    const d = W.__RB_SHIP_RATES__;
    return d && typeof d === 'object' ? d : scrapeRates(panel);
  };

  // write-only-on-change: never touch the DOM when the value is already right
  const setText = (el, s) => { if (el && el.textContent !== s) el.textContent = s; };
  const togglePart = (panel, key, on) => { const el = panel.querySelector('[data-rb-part="' + key + '"]'); if (el && el.hidden === on) el.hidden = !on; };

  /* ---------- branches (Federal's five, A325; SA moved to Wingfield 08-10, A822) ---------- */
  const RB_BRANCHES = {"NSW":{"capital":"Sydney, NSW","city":"Sydney","suburb":"Moorebank, NSW"},"VIC":{"capital":"Melbourne, VIC","city":"Melbourne","suburb":"Truganina, VIC"},"QLD":{"capital":"Brisbane, QLD","city":"Brisbane","suburb":"Stapylton, QLD"},"SA":{"capital":"Adelaide, SA","city":"Adelaide","suburb":"Wingfield, SA"},"WA":{"capital":"Perth, WA","city":"Perth","suburb":"Welshpool, WA"}};
  const RB_METRO = {"NSW":[[2000,2249],[2555,2574],[2745,2770]],"VIC":[[3000,3207],[3335,3341],[3427,3442],[3750,3810]],"QLD":[[4000,4207],[4208,4287],[4300,4305],[4500,4520]],"SA":[[5000,5199]],"WA":[[6000,6199]]};
  const RB_STATES = {"NSW":[[1000,2599],[2620,2899],[2921,2999]],"VIC":[[3000,3999],[8000,8999]],"QLD":[[4000,4999],[9000,9999]],"SA":[[5000,5999]],"WA":[[6000,6999]]};
  // Sydney first: the usual fallback origin. ☠️ A858 (Tommy 14-10): Perth is NOT a dispatch origin - "FED doesn't transfer
  // stock back from Perth". The hub publishes a Perth-only unit as sold out; this list must never say it ships from Perth.
  // Perth stays in the stock rows above as information only.
  const RB_DISPATCH_ORDER = ['NSW', 'VIC', 'QLD', 'SA'];
  // postcode -> the app's State code (the live cart's A680 bands)
  const STATE_BANDS = [[200, 299, 'ACT'], [800, 999, 'NT'], [1000, 2599, 'NSW'], [2600, 2618, 'ACT'], [2619, 2619, 'NSW'], [2620, 2899, 'NSW'], [2900, 2920, 'ACT'], [2921, 2999, 'NSW'], [3000, 3999, 'VIC'], [4000, 4999, 'QLD'], [5000, 5999, 'SA'], [6000, 6999, 'WA'], [7000, 7999, 'TAS'], [8000, 8999, 'VIC'], [9000, 9999, 'QLD']];
  const stateOf = (pc) => { const n = parseInt(pc, 10); for (const b of STATE_BANDS) if (n >= b[0] && n <= b[1]) return b[2]; return ''; };
  const bandLookup = (table, postcode) => {
    const pc = parseInt(postcode, 10);
    if (!isFinite(pc)) return '';
    for (const code of Object.keys(table)) for (const b of table[code]) if (pc >= b[0] && pc <= b[1]) return code;
    return '';
  };

  /* ---------- one panel ---------- */
  const bind = (panel) => {
    if (panel.cpdBound) return;
    panel.cpdBound = true;
    // 14-10-2026 (hub A859): a pickup-only product (the snippet, product tagged 'pickup-only') has no delivery to price.
    // The panel already says "Collection only" in Liquid; the one job left is to keep the rates app's own calculator
    // (its block sits right after this one in the template) off the page, because for this product the app answers
    // "no rates" everywhere but Brisbane, and an empty calculator reads like a fault.
    if (panel.hasAttribute('data-cpd-pickup-only')) {
      let tries = 0;
      const hideApp = () => {
        const c = D.querySelector('.oct-calculator-container, .js--calculator-product-container');
        if (!c) return false;
        const block = c.closest('.cpp-app') || c.closest('.shopify-app-block') || c;
        block.hidden = true;
        block.style.display = 'none';
        block.setAttribute('aria-hidden', 'true');
        return true;
      };
      const again = () => { if (hideApp()) return; if (++tries < 60) setTimeout(again, 150); };
      again();
      return;
    }
    const root = panel.closest('[data-bop]') || D;
    const isFed = panel.getAttribute('data-cpd-fed') !== '0';
    const slot = panel.querySelector('[data-delivery-calc-slot]');
    const form = panel.querySelector('[data-cpd-form]');
    const input = panel.querySelector('[data-cpd-input]');
    const msg = panel.querySelector('[data-cpd-msg]');
    const where = panel.querySelector('[data-cpd-where]');
    const whereText = panel.querySelector('[data-cpd-where-text]');
    const say = (t) => setText(msg, t || '');

    const calc = () => (slot && slot.querySelector('.oct-calculator-container')) || D.querySelector('.oct-calculator-container');
    // the app block lands wherever the editor put it: move it INTO the panel (live fillPanel)
    const fillPanel = () => {
      if (!slot || slot.getAttribute('data-filled')) return !!calc();
      const c = D.querySelector('.oct-calculator-container, .js--calculator-product-container');
      if (!c) return false;
      const block = c.closest('.cpp-app') || c.closest('.shopify-app-block') || c;
      slot.appendChild(block);
      slot.setAttribute('data-filled', '1');
      return true;
    };

    const currentVariantId = () => {
      const el = root.querySelector('[data-bop-variant-id]') || root.querySelector('form[data-bop-form] [name="id"]');
      const v = el ? String(el.value || '') : '';
      return /^\d+$/.test(v) ? v : '';
    };
    // the postcode the rates were quoted against: the app's RESOLVED address first (never a half-typed box)
    const shopperPostcode = () => {
      const c = calc();
      const el = c && c.querySelector('.oct-calculator-result-address-text');
      const m = el ? (el.textContent || '').match(/\b(\d{4})\b/) : null;
      if (m) return m[1];
      return '';
    };

    /* warehouse rows (A325d: repaint on a size change; a variant with no figures hides AND wipes its rows) */
    const paintWarehouse = () => {
      const wh = panel.querySelector('[data-rb-wh]');
      if (!wh) return;
      let map;
      try { map = JSON.parse(wh.getAttribute('data-rb-wh-map') || 'null'); } catch (e) { return; }
      if (!map) return;
      const vid = currentVariantId();
      if (!vid) { if (!wh.hidden) wh.hidden = true; wh.removeAttribute('data-rb-wh-painted'); return; }
      if (wh.getAttribute('data-rb-wh-painted') === vid) return;
      const qty = map[vid];
      if (!qty) {
        wh.querySelectorAll('[data-rb-branch]').forEach((r) => { if (r.getAttribute('data-rb-qty') !== '0') r.setAttribute('data-rb-qty', '0'); });
        if (!wh.hidden) wh.hidden = true;
        wh.setAttribute('data-rb-wh-painted', vid);
        return;
      }
      if (wh.hidden) wh.hidden = false;
      wh.querySelectorAll('[data-rb-branch]').forEach((r) => {
        let n = parseInt(qty[r.getAttribute('data-rb-branch')], 10);
        if (!isFinite(n) || n < 0) n = 0;
        if (r.getAttribute('data-rb-qty') !== String(n)) r.setAttribute('data-rb-qty', String(n));
        r.classList.toggle('is-stocked', n > 0);
        // A325h: every Federal branch says its number, zeros too. 28-10 (CF review TRU-03, Brain #714): a brand with its
        // own warehouses (Bromic: its report has no quantities, the hub writes a nominal 5) says "In stock" / "Not held
        // here", never a number.
        setText(r.querySelector('.cpd-wh__qty'), isFed ? n + ' in stock' : (n > 0 ? 'In stock' : 'Not held here'));
      });
      wh.setAttribute('data-rb-wh-painted', vid);
    };
    const branchQty = (code) => {
      const r = panel.querySelector('[data-rb-branch="' + code + '"]');
      const n = r ? parseInt(r.getAttribute('data-rb-qty') || '0', 10) : 0;
      return isFinite(n) ? n : 0;
    };
    const whVisible = () => { const wh = panel.querySelector('[data-rb-wh]'); return !!(wh && !wh.hidden); };
    const collectableBranch = () => {
      if (!isFed || !whVisible()) return '';
      const code = bandLookup(RB_METRO, shopperPostcode());
      if (!code) return '';
      return branchQty(code) > 0 ? code : '';
    };
    const anyBranchHasStock = () => {
      if (!isFed || !whVisible()) return false;
      return RB_DISPATCH_ORDER.some((c) => branchQty(c) > 0);
    };
    // A325f: where it would ship from; with no postcode only when ONE branch holds it (a wrong origin is worse than none)
    const dispatchBranch = () => {
      if (!whVisible()) return '';
      const stocked = RB_DISPATCH_ORDER.filter((c) => branchQty(c) > 0);
      if (!stocked.length) return '';
      if (stocked.length === 1) return stocked[0];
      const pc = shopperPostcode();
      if (!pc) return '';
      const home = bandLookup(RB_STATES, pc);
      if (home && stocked.indexOf(home) >= 0) return home;
      return stocked.indexOf('NSW') >= 0 ? 'NSW' : stocked[0];
    };

    /* A583: who speaks for the collect tile: 'branch' > 'rate' > 'prompt' > 'denied' > 'none' */
    const collectSource = (branchCode, rate, pc, stocked) => {
      if (branchCode) return 'branch';
      if (rate) return 'rate';
      if (!pc && stocked) return 'prompt';
      if (pc && stocked) return 'denied';
      return 'none';
    };
    const paintCollect = (data) => {
      const tile = panel.querySelector('[data-rb-tile="collect"]');
      if (!tile) return '';
      const code = collectableBranch();
      const b = code ? RB_BRANCHES[code] : null;
      const pc = shopperPostcode();
      const stocked = anyBranchHasStock();
      const rate = (data && data.collect) || null;
      const src = collectSource(code, rate, pc, stocked);
      const prompt = src === 'prompt';
      const denied = src === 'denied';
      let reason = '';
      if (denied) {
        const metro = bandLookup(RB_METRO, pc);
        reason = metro ? 'No stock at the ' + ((RB_BRANCHES[metro] || {}).city || 'nearest') + ' warehouse' : 'Not available in your area';
      }
      const qual = tile.querySelector('[data-rb-collect-qual]');
      if (panel.getAttribute('data-rb-csrc') !== src) panel.setAttribute('data-rb-csrc', src);
      if (src === 'rate') {
        setText(tile.querySelector('[data-rb-collect-ratename]'), rate.name || '');
        setText(qual, '');
        setText(panel.querySelector('[data-rb-collect-ratedetail]'), rate.desc || '');
      } else if (b) {
        panel.querySelectorAll('[data-rb-collect]').forEach((f) => setText(f, b[f.getAttribute('data-rb-collect')] || ''));
        setText(qual, 'From next business day, once confirmed');   // A331: "From": the earliest, never a date
      }
      if (prompt) { setText(tile.querySelector('[data-rb-collect="capital"]'), 'a warehouse'); setText(qual, 'Enter your postcode to check'); }
      else if (denied) { setText(tile.querySelector('[data-rb-collect="capital"]'), 'a warehouse'); setText(qual, reason); }
      const state = (src === 'branch' || src === 'rate') ? 'offer' : (prompt ? 'prompt' : (denied ? 'denied' : 'none'));
      if (tile.getAttribute('data-rb-cstate') !== state) tile.setAttribute('data-rb-cstate', state);
      if (tile.getAttribute('data-rb-prompt') !== (prompt ? '1' : '0')) tile.setAttribute('data-rb-prompt', prompt ? '1' : '0');
      const show = state !== 'none';
      if (tile.hidden === show) tile.hidden = !show;
      return src === 'rate' ? 'rate' : code;
    };
    const paintExpress = (data) => {
      const tile = panel.querySelector('[data-rb-tile="express"]');
      if (!tile) return false;
      const rate = data.express;
      if (rate) {
        const nm = (rate.name || '').replace(/\s*\([^()]*\)\s*$/, '').trim();
        setText(tile.querySelector('[data-rb-express-name]'), nm || 'Express Delivery');
      }
      const show = !!rate;
      if (tile.hidden === show) tile.hidden = !show;
      return show;
    };
    // A307l / A330 / A333 / A334 / A344: one service's price on its own tile; returns whether it is genuinely free
    const paintTilePrice = (tile, rate, known) => {
      const el = tile.querySelector('[data-rb-tprice]');
      if (!el) return false;
      let txt = '', soft = false, free = false;
      if (rate) {
        const isStandard = tile.getAttribute('data-rb-tile') === 'standard';
        const saysFree = /\bfree\b/i.test(rate.name || '');
        if (rate.quoted || rate.amount === null) { txt = 'Calculated after ordering'; soft = true; }
        else if (rate.amount === 0) {
          if (isStandard && saysFree) { txt = 'FREE Delivery'; free = true; }
          else { txt = 'Calculated after ordering'; soft = true; }
        } else if (rate.text) { txt = rate.text; }
      } else if (known) {
        // A331: say what to do; the words match the button's real label
        txt = 'Quote required: use Add to quote below'; soft = true;
      }
      // A335: real rates came back without this service = we do not sell it for this product: hide the tile
      const own = tile.getAttribute('data-rb-tile');
      if (own !== 'collect' && own !== 'express') {
        const offered = !(known && !rate);
        if (tile.hidden === offered) tile.hidden = !offered;
      }
      const cls = 'cpd-tile__price' + (soft ? ' cpd-tile__price--soft' : '') + (free ? ' cpd-tile__price--free cpd-tile__price--freeship' : '');
      // a real amount follows the EX / INC GST switch like every other price on the page (the rate is what checkout
      // charges, inc GST; ex = inc / 1.1, the same rule as the product price). Words are printed as they are.
      const amountCents = rate && !soft && !free && rate.amount > 0 ? Math.round(rate.amount * 100) : 0;
      const key = amountCents ? 'm' + amountCents : 't' + txt;
      if (el.getAttribute('data-k') !== key) {
        el.setAttribute('data-k', key);
        if (amountCents) {
          el.innerHTML = '<span class="ce-gst-ex">' + money(Math.round(amountCents * 10 / 11)) + '</span><span class="ce-gst-inc">' + money(amountCents) + '</span> <span class="cpd-tile__basis"><span class="ce-gst-ex">ex GST</span><span class="ce-gst-inc">inc GST</span></span>';
        } else {
          el.textContent = txt;
        }
      }
      if (el.className !== cls) el.className = cls;
      if (el.hidden === !!txt) el.hidden = !txt;
      return free;
    };
    // A392: the FREE DELIVERY pill by the price MIRRORS the standard tile's verdict and has no rule of its own
    const placeFreePill = (free) => {
      const host = root.querySelector('[data-cpd-freepill-slot]');
      if (!host) return;
      let pill = host.querySelector('[data-rb-freepill]');
      if (!free) { if (pill) host.removeChild(pill); return; }
      if (!pill) {
        pill = D.createElement('span');
        pill.className = 'cpd-freepill';
        pill.setAttribute('data-rb-freepill', '');
        pill.innerHTML = '<span class="cpd-freepill__lead">FREE DELIVERY</span><span class="cpd-freepill__qual"></span>';
        host.appendChild(pill);
      }
      const pc = shopperPostcode();
      setText(pill.querySelector('.cpd-freepill__qual'), 'Standard kerbside' + (pc ? ' to ' + pc : ''));
    };
    // A587: the fact line under the tiles; never on a collection (its own paragraphs say it)
    const etaFor = (sel, data, rate) => {
      if (data.eta) return data.eta;
      if (sel === 'collect') return '';
      return (rate && (rate.transit || rate.desc)) || '';
    };
    const setFact = (key, value) => {
      const el = panel.querySelector('[data-rb-fact="' + key + '"]');
      if (!el) return;
      if (value) { setText(el.querySelector('[data-rb-fact-text]'), value); if (el.hidden) el.hidden = false; }
      else if (!el.getAttribute('data-rb-from-liquid')) { if (!el.hidden) el.hidden = true; }
    };

    const paintShip = () => {
      let sel = panel.getAttribute('data-rb-method') || 'standard';
      const data = shipData(panel) || {};
      let rate = data[sel];
      // three states: unknown (no rows yet), priced, and quoted (rates came back without this service)
      const known = !!(data.standard || data.premium || data.express);
      paintWarehouse();
      const collectCode = paintCollect(data);
      if (sel === 'collect' && !collectCode) { sel = 'standard'; panel.setAttribute('data-rb-method', sel); rate = data[sel]; }
      const hasExpress = paintExpress(data);
      if (sel === 'express' && !hasExpress) { sel = 'standard'; panel.setAttribute('data-rb-method', sel); rate = data[sel]; }
      panel.classList.toggle('cpd--scraped', known);
      panel.querySelectorAll('[data-rb-tile]').forEach((t) => {
        const on = t.getAttribute('data-rb-tile') === sel;
        t.classList.toggle('is-on', on);
        if (t.getAttribute('aria-checked') !== (on ? 'true' : 'false')) t.setAttribute('aria-checked', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        const wasFree = paintTilePrice(t, data[t.getAttribute('data-rb-tile')], known);
        if (t.getAttribute('data-rb-tile') === 'standard') placeFreePill(wasFree);
      });
      panel.querySelectorAll('[data-rb-detail]').forEach((d) => { const show = d.getAttribute('data-rb-detail') === sel; if (d.hidden === show) d.hidden = !show; });
      const prem = data.premium;
      const premPriced = !!(prem && !prem.quoted && prem.amount);
      togglePart(panel, 'prem-priced', premPriced);
      togglePart(panel, 'prem-quote', !premPriced);
      setFact('eta', etaFor(sel, data, rate));
      const dcode = dispatchBranch();
      const dbranch = dcode ? RB_BRANCHES[dcode] : null;
      setFact('origin', data.origin || (dbranch && isFed ? dbranch.capital : ''));
      // where the rates were quoted to
      const c = calc();
      const addr = c && c.querySelector('.oct-calculator-result-address-text');
      const atext = addr ? (addr.textContent || '').trim() : '';
      if (where) {
        if (atext && known) { setText(whereText, 'To ' + atext); if (where.hidden) where.hidden = false; }
        else if (!where.hidden) where.hidden = true;
      }
    };

    /* the "?" bubbles (A325c / A336 / A748): tap opens, under the pressed tile's row */
    const body = panel.querySelector('[data-rb-help-body]');
    const placeHelp = (tile) => {
      const grid = tile && tile.parentNode;
      if (!body || !grid || !grid.classList || !grid.classList.contains('cpd-tiles')) return;
      const wasHidden = body.hidden;
      body.hidden = true;
      const top = tile.offsetTop;
      let last = tile;
      Array.prototype.forEach.call(grid.children, (k) => {
        if (k === body || k.hidden || !k.hasAttribute('data-rb-tile')) return;
        if (Math.abs(k.offsetTop - top) < 4) last = k;
      });
      if (last.nextSibling !== body) grid.insertBefore(body, last.nextSibling);
      body.hidden = wasHidden;
      const tr = tile.getBoundingClientRect(), br = body.getBoundingClientRect();
      if (br.width) body.style.setProperty('--cpd-caret', Math.round(tr.right - br.left - 18) + 'px');
    };
    panel.addEventListener('click', (e) => {
      const mark = e.target.closest && e.target.closest('[data-rb-tilehelp]');
      const t = e.target.closest && e.target.closest('[data-rb-tile]');
      if (!t || !panel.contains(t)) return;
      e.preventDefault();
      if (t.getAttribute('data-rb-cstate') === 'denied') return;
      if (t.getAttribute('data-rb-prompt') === '1') {
        // the collect PROMPT is an invitation to give a postcode, not a service to choose
        if (input && form && !form.hidden) { input.focus(); input.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
        return;
      }
      // read BEFORE the selection moves: "?" on the tile that is already chosen, with its bubble open, closes it
      const wasOpen = !!(body && !body.hidden);
      const wasChosen = t.getAttribute('aria-checked') === 'true';
      panel.setAttribute('data-rb-method', t.getAttribute('data-rb-tile'));
      paintShip();
      if (!body) return;
      if (mark) {
        const open = !(wasOpen && wasChosen);
        if (body.hidden === open) body.hidden = !open;
        if (open) placeHelp(t);
        panel.setAttribute('data-rb-helpseen', '1');      // A748: they found the "?": the marks stop beating
      } else if (!body.hidden) {
        placeHelp(t);                                     // an open bubble follows the chosen tile to its row
      }
      // choosing collection opens its detail straight away (the suburb and the don't-travel-yet warning)
      if (t.getAttribute('data-rb-tile') === 'collect' && body.hidden) { body.hidden = false; placeHelp(t); }
    });
    panel.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      const t = e.target.closest && e.target.closest('[data-rb-tile]');
      if (!t) return;
      e.preventDefault();
      const all = Array.prototype.filter.call(panel.querySelectorAll('[data-rb-tile]'), (el) => !el.hidden);
      if (!all.length) return;
      const i = all.indexOf(t);
      const next = all[(i + (e.key === 'ArrowRight' ? 1 : all.length - 1)) % all.length];
      panel.setAttribute('data-rb-method', next.getAttribute('data-rb-tile'));
      paintShip();
      next.focus();
    });
    W.addEventListener('resize', () => { if (body && !body.hidden) placeHelp(panel.querySelector('[data-rb-tile][aria-checked="true"]')); });

    /* ---------- the postcode box drives the app's own form ---------- */
    const wait = (test, ms) => new Promise((resolve) => {
      const t0 = Date.now();
      const tick = () => { const r = test(); if (r) return resolve(r); if (Date.now() - t0 > ms) return resolve(null); setTimeout(tick, 120); };
      tick();
    });
    const setVal = (el, v) => {
      const proto = Object.getPrototypeOf(el);
      const d = proto && Object.getOwnPropertyDescriptor(proto, 'value');
      if (d && d.set) d.set.call(el, v); else el.value = v;
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    };
    const stepAside = (why) => {
      // the app shows itself again: the shopper can still price a postcode with its own form
      panel.removeAttribute('data-cpd-driven');
      if (form) form.hidden = true;
      panel.setAttribute('data-cpd-fallback', why || '1');
    };
    let running = false;
    const run = async (pc) => {
      if (running) return;
      const code = stateOf(pc);
      if (!/^\d{4}$/.test(pc) || !code) { say('Please enter a 4-digit Australian postcode.'); return; }
      const c = calc();
      if (!c) { say(''); return; }
      running = true;
      panel.classList.add('is-busy');
      const goBtn = panel.querySelector('[data-cpd-go]');
      if (goBtn) goBtn.classList.add('is-loading');
      say('Checking ' + pc + ' ...');
      try {
        // a result already showing: ask for the form back
        if (!c.querySelector('.oct-calculator-field-zip')) {
          const change = c.querySelector('.oct-change-address, .oct-calculator-result-address-button-reset');
          if (change) change.click();
          else { const open = c.querySelector('.oct-calculator-open'); if (open) open.click(); }
        }
        const zip = await wait(() => c.querySelector('.oct-calculator-field-zip, input[name="zip"]'), 4000);
        const prov = c.querySelector('.oct-calculator-field-province, select[name="province"]');
        const btn = c.querySelector('.js--calculate, .oct-calculator-button-calculate');
        if (!zip || !btn) { stepAside('fields'); say('Please use the calculator below.'); return; }
        if (prov) {
          const opt = Array.prototype.find.call(prov.options, (o) => o.value === code);
          if (opt) setVal(prov, code);
        }
        setVal(zip, pc);
        // the old rows go first, so a new answer can be told from the last one
        c.querySelectorAll('.oct-shipping-rate').forEach((r) => r.setAttribute('data-cpd-old', '1'));
        btn.click();
        const done = await wait(() => {
          const rows = c.querySelectorAll('.oct-shipping-rate:not([data-cpd-old])');
          if (rows.length) return 'rates';
          const err = c.querySelector('.oct-calculator-form-message-error, .oct-calculator-error, [class*="message-error"]');
          if (err && (err.textContent || '').trim()) return 'error';
          const res = c.querySelector('.oct-calculator-result-container');
          if (res && !c.querySelector('.oct-shipping-rate')) return 'empty';
          return null;
        }, 20000);
        if (done === 'rates') {
          say('');
          try { localStorage.setItem('ce-postcode', pc); } catch (e) { /* private mode */ }
        } else if (done === 'error' || done === 'empty') {
          const err = c.querySelector('.oct-calculator-form-message-error, .oct-calculator-error, [class*="message-error"], .oct-calculator-result-container');
          say((err && (err.textContent || '').trim().slice(0, 220)) || 'No delivery price came back for ' + pc + '. Use Add to quote and we will price it.');
        } else {
          say('That took too long. Please try again, or use Add to quote and we will price it.');
        }
        paintShip();
      } finally {
        running = false;
        panel.classList.remove('is-busy');
        const goBtn2 = panel.querySelector('[data-cpd-go]');
        if (goBtn2) goBtn2.classList.remove('is-loading');
      }
    };
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        run(String(input && input.value || '').replace(/\D/g, '').slice(0, 4));
      });
      if (input) input.addEventListener('input', () => { const v = input.value.replace(/\D/g, '').slice(0, 4); if (v !== input.value) input.value = v; });
    }
    const change = panel.querySelector('[data-cpd-change]');
    if (change) change.addEventListener('click', () => { if (form && !form.hidden && input) { input.focus(); input.select(); } });

    /* ---------- start ---------- */
    const start = () => {
      if (!fillPanel()) return false;
      if (form) {
        form.hidden = false;
        panel.setAttribute('data-cpd-driven', '1');
        try { const last = localStorage.getItem('ce-postcode'); if (last && input && !input.value) input.value = last; } catch (e) { /* fine */ }
      }
      // the app's rows arrive (and change) inside the slot: repaint on any change there
      if (slot && W.MutationObserver) {
        let t = 0;
        new MutationObserver(() => { clearTimeout(t); t = setTimeout(paintShip, 60); }).observe(slot, { childList: true, subtree: true, characterData: true });
      }
      paintShip();
      return true;
    };
    if (!start()) {
      // the app block draws late (or is not on this template): look for a few seconds, then paint without it
      let tries = 0;
      // no app after ~6 s: the heading row gives back the room it kept for the postcode box (data-cpd-noapp)
      const again = () => { if (start()) return; if (++tries < 40) setTimeout(again, 150); else { panel.setAttribute('data-cpd-noapp', '1'); paintShip(); } };
      again();
    }
    root.addEventListener('ce:variant:change', () => { const wh = panel.querySelector('[data-rb-wh]'); if (wh) wh.removeAttribute('data-rb-wh-painted'); paintShip(); });
    D.addEventListener('rb:ship', paintShip);
  };

  const init = (scope) => { (scope && scope.querySelectorAll ? scope : D).querySelectorAll('[data-cpd]').forEach(bind); };
  W.CEDelivery = { init, methodOf, transitFrom };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', () => init(D)); else init(D);
  D.addEventListener('shopify:section:load', (e) => init(e.target));
})();
